import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";

/**
 * Header nav item with a dropdown panel.
 * - Opens on hover (desktop) and on click (touch / keyboard).
 * - Closes on outside click, Escape key, route change, or item click.
 * - Uses a small grace delay + an invisible bridge to bridge the gap between
 *   the button and the panel, so dropdowns with few items don't snap shut
 *   when the cursor moves down to reach them.
 */
export default function NavDropdown({ label, items, testId, parentMatchPrefixes = [] }) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef(null);
  const closeTimer = React.useRef(null);
  const location = useLocation();

  const openNow = React.useCallback(() => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpen(true);
  }, []);
  const scheduleClose = React.useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 180);
  }, []);

  React.useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);

  // Close on route change
  React.useEffect(() => { setOpen(false); }, [location.pathname, location.search, location.hash]);

  // Close on outside click + Escape
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const isActive = parentMatchPrefixes.some((p) =>
    location.pathname === p || location.pathname.startsWith(p + "/") || (location.pathname + location.search).startsWith(p)
  );

  return (
    <div
      ref={rootRef}
      className="relative"
      onMouseEnter={openNow}
      onMouseLeave={scheduleClose}
      data-testid={`nav-${testId}-wrap`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        onFocus={openNow}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`px-3 py-2 text-sm font-bold rounded-md border-2 transition whitespace-nowrap inline-flex items-center gap-1 ${
          isActive ? "bg-cef-blue text-white border-black shadow-brutal" : "border-transparent hover:border-black hover:bg-cef-yellow"
        }`}
        data-testid={`nav-${testId}`}
      >
        {label} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>

      {open && (
        <div
          role="menu"
          // top-full + pt-2 → the hoverable area touches the button (no gap), the visible card starts 8px lower.
          className="absolute left-0 top-full pt-2 w-72 z-50"
          onMouseEnter={openNow}
          onMouseLeave={scheduleClose}
          data-testid={`nav-${testId}-panel`}
        >
          <div className="bg-white border-2 border-black shadow-brutalLg rounded-md overflow-hidden">
            <ul className="grid">
              {items.map((it) => (
                <li key={`${it.to}-${it.testid}`} className="border-b-2 border-black/10 last:border-b-0">
                  <NavLink
                    to={it.to}
                    role="menuitem"
                    end={it.end}
                    onClick={() => setOpen(false)}
                    className={({ isActive: a }) =>
                      `flex items-start gap-3 px-4 py-3 hover:bg-cef-yellow transition ${a ? "bg-cef-yellow" : ""}`
                    }
                    data-testid={`nav-${testId}-item-${it.testid}`}
                  >
                    {it.Icon && (
                      <span className={`w-9 h-9 inline-flex items-center justify-center border-2 border-black rounded-md shrink-0 ${it.color || "bg-white"}`} aria-hidden="true">
                        <it.Icon className="w-4 h-4" strokeWidth={2.5} />
                      </span>
                    )}
                    <span className="flex-1 min-w-0">
                      <span className="block font-bold text-sm leading-tight">{it.label}</span>
                      {it.desc && <span className="block text-xs text-neutral-600 mt-0.5">{it.desc}</span>}
                    </span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
